// pages/Invitations.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiCalendar, 
  FiMapPin, 
  FiClock,
  FiUsers,
  FiEdit2,
  FiBarChart2,
  FiPlusCircle,
  FiRefreshCw
} from 'react-icons/fi';
import {FaTicketAlt } from 'react-icons/fa'
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../services/auth';
import supabase from '../services/supabase';
import '../styles/Invitations.scss';
import { firstPageParam, nextPageParam, sectionsFromRows } from '../shared/homePaging';

const InvitationCard = ({ event, isUserHost, onEdit, onViewSales }) => {
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return 'Date not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const time = new Date(`2000-01-01T${timeString}`);
    return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getTimeDisplay = () => {
    const start = formatTime(event.event_time);
    const end = formatTime(event.end_time);
    if (start && end) return `${start} - ${end}`;
    if (start) return start;
    return '';
  };

  const getImageUrl = () => {
    return event.cover_image_cdn_url || 
           event.gif_cover_cdn_url || 
           event.cover_image_direct_url || 
           '/images/events/1.jpg';
  };

  const isPaidEvent = event.pass_type === 'paid' || 
                      (event.passes && event.passes.some(p => parseFloat(p.price) > 0));

  const handleCardClick = () => {
    // Use shareable_slug if available, otherwise use id
    const slug = event.shareable_slug || event.id;
    navigate(`/i/${slug}`);
  };

  return (
    <motion.div
      className="invitation-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      onClick={handleCardClick}
    >
      <div className="invitation-image">
        <img src={getImageUrl()} alt={event.title} />
        <div className="invitation-overlay"></div>

        {isUserHost && (
          <div className="action-buttons">
            {isPaidEvent && (
              <button
                className="action-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewSales(event);
                }}
              >
                <FiBarChart2 />
              </button>
            )}
            <button
              className="action-btn"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(event);
              }}
            >
              <FiEdit2 />
            </button>
          </div>
        )}
      </div>

      <div className="invitation-content">
        <div className="invitation-badges">
          {isUserHost && (
            <span className="badge host-badge">Host</span>
          )}
          {/* Only the host sees a draft, so this is a reminder to finish it. */}
          {event.status === 'draft' && (
            <span className="badge draft-badge">Draft</span>
          )}
          {isPaidEvent && (
            <span className="badge paid-badge">
              <FaTicketAlt size={12} />
              Paid
            </span>
          )}
        </div>

        <h3 className="invitation-title">{event.title}</h3>

        <div className="invitation-details">
          <div className="detail-item">
            <FiCalendar size={14} />
            <span>{formatDate(event.event_date)}</span>
          </div>

          {getTimeDisplay() && (
            <div className="detail-item">
              <FiClock size={14} />
              <span>{getTimeDisplay()}</span>
            </div>
          )}

          <div className="detail-item">
            <FiMapPin size={14} />
            <span>{event.location_name || 'Location not set'}</span>
          </div>

          {event.attendee_count > 0 && (
            <div className="detail-item attendee-count">
              <FiUsers size={14} />
              <span>
                {event.attendee_count} {event.attendee_count === 1 ? 'guest' : 'guests'}
              </span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const EmptyState = ({ activeTab, onCreate }) => (
  <motion.div 
    className="empty-state"
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
  >
    {activeTab === 'hosting' ? (
      <FiPlusCircle size={60} />
    ) : (
      <FiCalendar size={60} />
    )}
    
    <h3>
      {activeTab === 'hosting' ? 'No created events' : 'No events yet'}
    </h3>
    
    <p>
      {activeTab === 'hosting'
        ? "You haven't created any events yet"
        : "You aren't attending any events yet"}
    </p>

    {activeTab === 'hosting' && (
      <button className="btn btn-primary create-event-btn" onClick={onCreate}>
        <FiPlusCircle />
        <span>Create Event</span>
      </button>
    )}
  </motion.div>
);

/**
 * Your invitations, a page at a time — the same list as the app's home.
 *
 * get_home_invitations returns one page already in display order: open date
 * polls, then upcoming soonest first, then past most recent first. Upcoming
 * and past share ONE pagination: the first 6 are whatever comes first in that
 * order, and each further load brings 4 more — so past invitations appear only
 * once someone scrolls down to them, instead of the whole history loading up
 * front as it used to.
 */
const Invitations = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const sentinelRef = useRef(null);
  const requestRef = useRef(0);
  // Armed by a real scroll, disarmed by each load — so a page arriving near
  // the bottom cannot trigger the next one, and the next, until everything
  // has loaded without anyone scrolling.
  const scrolledRef = useRef(false);

  const loadPage = useCallback(async (page, reset) => {
    if (!user) return;
    const req = ++requestRef.current;
    if (reset) setLoading(true); else setLoadingMore(true);
    try {
      const { data, error } = await supabase.rpc('get_home_invitations', {
        p_filter: activeTab, p_limit: page.limit, p_offset: page.offset,
      });
      if (error) throw error;
      // A tab switch mid-request: drop the stale answer.
      if (req !== requestRef.current) return;
      const next = { rows: data || [], limit: page.limit };
      setPages((prev) => {
        const all = reset ? [next] : [...prev, next];
        setHasMore(!!nextPageParam(next, all));
        return all;
      });
    } catch (err) {
      console.error('Error loading invitations:', err);
      if (reset) setPages([]);
      setHasMore(false);
    } finally {
      if (req === requestRef.current) {
        setLoading(false);
        setLoadingMore(false);
        setRefreshing(false);
      }
    }
  }, [user, activeTab]);

  useEffect(() => {
    if (!user) return;
    setPages([]);
    loadPage(firstPageParam, true);
  }, [user, activeTab, loadPage]);

  useEffect(() => {
    const onScroll = () => { scrolledRef.current = true; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore || pages.length === 0) return;
    const pageFits = document.documentElement.scrollHeight <= window.innerHeight;
    // A page too short to scroll fills to the screen; otherwise wait for a scroll.
    if (!pageFits && !scrolledRef.current) return;
    scrolledRef.current = false;
    const param = nextPageParam(pages[pages.length - 1], pages);
    if (param) loadPage(param, false);
  }, [hasMore, loading, loadingMore, pages, loadPage]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) loadMore(); },
      { rootMargin: '300px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    loadPage(firstPageParam, true);
  }, [loadPage]);

  const handleEdit = useCallback((event) => {
    navigate(`/i/${event.shareable_slug || event.id}`);
  }, [navigate]);

  const handleViewSales = useCallback(() => {
    alert('Sales analytics page coming soon! Check the mobile app for full analytics.');
  }, []);

  const handleCreateEvent = useCallback(() => {
    navigate('/create-event');
  }, [navigate]);

  const sections = sectionsFromRows(pages.flatMap((p) => p.rows));
  const empty = !loading && sections.length === 0;

  return (
    <DashboardLayout>
      <div className="invitations-page">
        <div className="invitations-container">
          <motion.div
            className="invitations-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="header-content">
              <h1>Your Invitations</h1>
              <button className="refresh-btn" onClick={handleRefresh} disabled={refreshing} aria-label="Refresh">
                <FiRefreshCw className={refreshing ? 'spinning' : ''} />
              </button>
            </div>

            <div className="tabs-container" role="tablist">
              {[
                { key: 'all', label: 'All' },
                { key: 'hosting', label: 'Hosting' },
                { key: 'attending', label: 'Going' },
              ].map((t) => (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={activeTab === t.key}
                  className={`tab ${activeTab === t.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </motion.div>

          <div className="invitations-content">
            {loading && !refreshing ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading invitations...</p>
              </div>
            ) : empty ? (
              <EmptyState activeTab={activeTab} onCreate={handleCreateEvent} />
            ) : (
              <>
                {sections.map((section) => (
                  <div key={section.key} className="invitations-section">
                    {/* Only label the groups when there is more than one. */}
                    {sections.length > 1 && (
                      <h2 className="invitations-section-title">{section.title}</h2>
                    )}
                    <div className="invitations-grid">
                      {section.data.map((event) => (
                        <InvitationCard
                          key={event.id}
                          event={event}
                          isUserHost={!!event.is_hosting}
                          onEdit={handleEdit}
                          onViewSales={handleViewSales}
                        />
                      ))}
                    </div>
                  </div>
                ))}
                <div ref={sentinelRef} aria-hidden="true" style={{ height: 1 }} />
                {loadingMore && (
                  <div className="loading-container"><div className="spinner"></div></div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Invitations;